import { Component } from '@theme/component';
import { debounce, onDocumentLoaded, setHeaderMenuStyle } from '@theme/utilities';
import { MegaMenuHoverEvent } from '@theme/events';

/**
 * A custom element that manages a header menu.
 *
 * @typedef {Object} State
 * @property {HTMLElement | null} activeItem - The currently active menu item.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} overflowMenu - The overflow menu.
 * @property {HTMLElement[]} [submenu] - The submenu in each respective menu item.
 *
 * @extends {Component<Refs>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  #submenuUnhoverDelayMs = 140;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #deactivateTimeout = null;

  /**
   * @type {ResizeObserver | null}
   */
  #submenuResizeObserver = null;

  /**
   * Pending double-rAF id for the first settled submenu-height write.
   * @type {number}
   */
  #submenuHeightRafId = 0;

  connectedCallback() {
    super.connectedCallback();

    onDocumentLoaded(this.#preloadImages);
    window.addEventListener('resize', this.#resizeListener);
    this.overflowMenu?.addEventListener('pointerleave', this.#overflowSubmenuListener);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#resizeListener);
    document.body.removeEventListener('pointermove', this.#onPointerMove);
    if (this.#state.activeItem) {
      this.#stopPointerTracking(this.#state.activeItem);
    }
    this.overflowMenu?.removeEventListener('pointerleave', this.#overflowSubmenuListener);
    this.#clearDeactivateTimeout();
    this.#removeDismissListeners();
    this.#cleanupSubmenuHeightObserver();
  }

  /**
   * Debounced resize event listener to recalculate menu style
   */
  #resizeListener = debounce(() => {
    setHeaderMenuStyle();

    // Always collapse active menu state on resize, then re-assert after menu style settles.
    this.#deactivate(this.#state.activeItem, {force: true});

    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        this.#deactivate(this.#state.activeItem, {force: true});
      });
    });
  }, 100);

  #overflowSubmenuListener = () => {
    if (this.#isDesktopClickMode) return;

    this.#deactivate();
  };

  /**
   * @type {State}
   */
  #state = {
    activeItem: null,
  };

  /**
   * @type {ReturnType<typeof setTimeout> | undefined}
   */
  #pointerIdleTimer;

  /**
   * Last known pointer position for Safari hit-test reconciliation.
   * @type {{ x: number, y: number }}
   */
  #lastPointer = { x: 0, y: 0 };

  /**
   * Update the safety box idle state on the active menu item.
   * @param {PointerEvent} event
   */
  #onPointerMove = (event) => {
    const activeLink = this.#state.activeItem;
    if (!activeLink) return;

    this.#lastPointer.x = event.clientX;
    this.#lastPointer.y = event.clientY;

    const moving = Math.abs(event.movementX) >= 1 || Math.abs(event.movementY) >= 1;
    activeLink.dataset.safetyBox = `${moving}`;

    clearTimeout(this.#pointerIdleTimer);
    if (moving) {
      this.#pointerIdleTimer = setTimeout(() => {
        if (this.#state.activeItem) {
          this.#state.activeItem.dataset.safetyBox = 'false';
          this.#reconcilePointerTarget();
        }
      }, 50);
    } else {
      this.#reconcilePointerTarget();
    }
  };

  /**
   * Check if the pointer is over a different menu item and trigger activation if so.
   * Works around Safari not re-evaluating hit targets after pseudo-element changes.
   */
  #reconcilePointerTarget() {
    const { x, y } = this.#lastPointer;
    requestAnimationFrame(() => {
      const target = document.elementFromPoint(x, y);
      if (!target) return;
      const listItem = target.closest('.menu-list__list-item');
      if (listItem && !listItem.contains(this.#state.activeItem)) {
        listItem.dispatchEvent(new PointerEvent('pointerenter', { bubbles: false }));
      }
    });
  }

  /**
   * Begin pointer tracking for the safety box on the newly active item.
   * @param {HTMLElement} item
   * @param {HTMLElement | null} previousItem
   */
  #startPointerTracking(item, previousItem) {
    if (previousItem) {
      this.#stopPointerTracking(previousItem);
    } else {
      document.body.addEventListener('pointermove', this.#onPointerMove);
    }

    const rect = item.getBoundingClientRect();
    const isOverlap = this.headerComponent?.hasAttribute('data-submenu-overlap-bottom-row');
    const boundary = isOverlap ? this.headerComponent?.querySelector('.header__row--top') : this.headerComponent;
    item.style.setProperty('--box-height', `${(boundary?.getBoundingClientRect().bottom ?? 0) - rect.top}px`);
  }

  /**
   * Stop pointer tracking and remove all safety box properties from an item.
   * @param {HTMLElement} item
   */
  #stopPointerTracking(item) {
    clearTimeout(this.#pointerIdleTimer);
    this.#pointerIdleTimer = undefined;
    item.style.removeProperty('--box-height');
    delete item.dataset.safetyBox;
  }

  /**
   * Get the overflow menu
   */
  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]'));
  }

  /**
   * Whether the overflow list is hovered
   * @returns {boolean}
   */
  get overflowListHovered() {
    return this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow-list"]')?.matches(':hover') ?? false;
  }

  get headerComponent() {
    return /** @type {HTMLElement | null} */ (this.closest('header-component'));
  }

  get #isDesktopClickMode() {
    return this.dataset.desktopInteraction === 'click' && this.headerComponent?.dataset.menuStyle === 'menu';
  }

  /**
   * Activate the selected menu item immediately
   * @param {PointerEvent | FocusEvent} event
   */
  activate = (event) => {
    if (this.#isDesktopClickMode && event instanceof PointerEvent) return;

    this.dispatchEvent(new MegaMenuHoverEvent());
    this.#clearDeactivateTimeout();

    if (!(event.target instanceof Element) || !this.headerComponent) return;

    let item = findMenuItem(event.target);

    if (!item || item == this.#state.activeItem) return;

    const listItem = event.target.closest('.menu-list__list-item');
    const isDefaultSlot = listItem ? listItem.slot === '' : true;
    this.#activateItem(item, isDefaultSlot);
  };

  /**
   * Toggle a menu item in desktop click mode.
   * @param {MouseEvent} event
   */
  toggle = (event) => {
    if (!this.#isDesktopClickMode || !(event.target instanceof Element)) return;

    const item = findMenuItem(event.target);

    if (!item) return;

    // Derive slot from the enclosing list item, not the button itself (which has no slot attribute)
    const listItem = event.target.closest('.menu-list__list-item');
    const isDefaultSlot = listItem ? listItem.slot === '' : true;
    const submenu = findSubmenu(item);
    const canToggle = Boolean(submenu) || !isDefaultSlot;

    if (!canToggle) return;

    if (item === this.#state.activeItem) {
      event.preventDefault();
      this.#deactivate(item, {force: true});
      return;
    }

    event.preventDefault();
    this.#activateItem(item, isDefaultSlot);
    this.#addDismissListeners();
  };

  /**
   * Add keyboard support for click-mode disclosure behavior on menu links.
   * @param {KeyboardEvent} event
   */
  handleMenuItemKeydown = (event) => {
    if (!this.#isDesktopClickMode || !(event.target instanceof Element)) return;

    const key = event.key;
    const isSpace = key === ' ' || key === 'Spacebar';
    const isArrowDown = key === 'ArrowDown';

    if (!isSpace && !isArrowDown) return;

    const item = findMenuItem(event.target);

    if (!item) return;

    const listItem = event.target.closest('.menu-list__list-item');
    const isDefaultSlot = listItem ? listItem.slot === '' : true;
    const submenu = findSubmenu(item);
    const canToggle = Boolean(submenu) || !isDefaultSlot;

    if (!canToggle) return;

    event.preventDefault();

    if (isSpace) {
      if (item === this.#state.activeItem) {
        this.#deactivate(item, {force: true});
        return;
      }

      this.#activateItem(item, isDefaultSlot);
      this.#addDismissListeners();
      return;
    }

    if (item !== this.#state.activeItem) {
      this.#activateItem(item, isDefaultSlot);
      this.#addDismissListeners();
    }

    if (submenu) {
      focusFirstFocusableElement(submenu);
    }
  };

  /**
   * Activate a menu item.
   * @param {HTMLElement} item
   * @param {boolean} isDefaultSlot
   */
  #activateItem(item, isDefaultSlot) {
    if (!this.headerComponent) return;

    const previouslyActiveItem = this.#state.activeItem;
    const previousSubmenu = findSubmenu(previouslyActiveItem);

    this.dataset.overflowExpanded = (!isDefaultSlot).toString();

    if (previouslyActiveItem) {
      previouslyActiveItem.ariaExpanded = 'false';
    }

    if (previousSubmenu) {
      delete previousSubmenu.dataset.active;
    }

    this.#state.activeItem = item;
    this.ariaExpanded = 'true';
    item.ariaExpanded = 'true';

    let submenu = findSubmenu(item);
    const hasSubmenu = Boolean(submenu);

    if (!hasSubmenu && !isDefaultSlot) {
      submenu = this.overflowMenu;
    }

    if (submenu) {
      // Mark submenu as active for content-visibility optimization
      submenu.dataset.active = '';
      this.#preloadImages(isDefaultSlot ? submenu : this);
      this.#observeSubmenuHeight(submenu, isDefaultSlot, hasSubmenu);
    } else {
      this.#applySubmenuHeight(0);
    }

    this.style.setProperty('--submenu-opacity', '1');
    this.#startPointerTracking(item, previouslyActiveItem);
  }

  /**
   * Deactivate the active item after a delay
   * @param {PointerEvent | FocusEvent} event
   */
  deactivate(event) {
    if (this.#isDesktopClickMode) return;

    if (!(event.target instanceof Element)) return;

    const activeItemContainer = this.#state.activeItem?.closest('.menu-list__list-item');
    const menu = findSubmenu(this.#state.activeItem);
    const isMovingWithinActiveItem = event.relatedTarget instanceof Node && activeItemContainer?.contains(event.relatedTarget);
    const isMovingToSubmenu = event.relatedTarget instanceof Node && menu?.contains(event.relatedTarget);
    const isMovingToOverflowMenu =
      event.relatedTarget instanceof Node && event.relatedTarget.parentElement?.matches('[slot="overflow"]');

    if (isMovingWithinActiveItem || isMovingToOverflowMenu || isMovingToSubmenu) {
      if (this.#state.activeItem) {
        this.#stopPointerTracking(this.#state.activeItem);
      }
      return;
    }

    this.#scheduleDeactivate();
  }

  #scheduleDeactivate() {
    this.#clearDeactivateTimeout();

    this.#deactivateTimeout = setTimeout(() => {
      this.#deactivate();
    }, this.#submenuUnhoverDelayMs);
  }

  #clearDeactivateTimeout() {
    if (!this.#deactivateTimeout) return;

    clearTimeout(this.#deactivateTimeout);
    this.#deactivateTimeout = null;
  }

  /**
   * Deactivate the active item immediately
   * @param {HTMLElement | null} [item]
   * @param {{ force?: boolean }} [options]
   */
  #deactivate = (item = this.#state.activeItem, options = {}) => {
    if (!item || item != this.#state.activeItem) return;

    const submenu = findSubmenu(item);
    const {force = false} = options;

    // Keep submenu open while either the active menu item or its submenu is hovered.
    if (!force && (item.matches(':hover') || submenu?.matches(':hover'))) return;

    // Don't deactivate if the overflow menu or overflow list is still being hovered
    if (!force && (this.overflowListHovered || this.overflowMenu?.matches(':hover'))) return;

    this.#cleanupSubmenuHeightObserver();
    this.#applySubmenuHeight(0);
    this.style.setProperty('--submenu-opacity', '0');
    this.dataset.overflowExpanded = 'false';
    this.#removeDismissListeners();

    document.body.removeEventListener('pointermove', this.#onPointerMove);
    this.#stopPointerTracking(item);

    this.#state.activeItem = null;
    this.ariaExpanded = 'false';
    item.ariaExpanded = 'false';

    // Remove active state from submenu after animation completes
    if (submenu) {
      delete submenu.dataset.active;
    }
  };

  #getOverflowListLinksHeight() {
    const slottedMenuLinks = this.overflowMenu?.querySelector('slot')?.assignedElements();
    if (!slottedMenuLinks) return this.overflowMenu?.offsetHeight || 0;

    /**
     * @param {(submenu: HTMLElement) => void} cb
     */
    const mapSubmenus = (cb) => {
      slottedMenuLinks.forEach((link) => {
        const submenu = /** @type {HTMLElement | null} */ (link.querySelector('[ref="submenu[]"]'));
        if (submenu) {
          cb(submenu);
        }
      });
    };

    mapSubmenus((submenu) => {
      submenu.style.setProperty('display', 'none');
    });
    const height = this.overflowMenu?.offsetHeight || 0;
    mapSubmenus((submenu) => {
      submenu.style.removeProperty('display');
    });
    return height;
  }

  /**
   * Apply both clip-path CSS variables together. `--full-open-header-height`
   * must stay in lockstep with `--submenu-height` or the panel clips short.
   * @param {number} height
   */
  #applySubmenuHeight(height) {
    if (!this.headerComponent) return;

    const nextHeight = `${height}px`;

    if (this.headerComponent.style.getPropertyValue('--submenu-height') === nextHeight) return;

    this.headerComponent.style.setProperty('--submenu-height', nextHeight);
    this.#setFullOpenHeaderHeight(height);
  }

  /**
   * Measure the open submenu (or overflow panel) height.
   * @param {HTMLElement | null | undefined} submenu
   * @param {boolean} isDefaultSlot
   * @param {boolean} hasSubmenu
   * @returns {number}
   */
  #measureSubmenuHeight(submenu, isDefaultSlot, hasSubmenu) {
    let finalHeight = submenu?.offsetHeight || 0;

    if (!isDefaultSlot) {
      const overflowListHeight = this.#getOverflowListLinksHeight();
      if (hasSubmenu) {
        /* Note: When the submenu is inside the overflow menu, its offsetHeight is not valid due to the lack of padding
         * we could add the padding variables to the submenu.offsetHeight, but measuring the overflowMenu.offsetHeight is just easier */
        const overflowHeight = this.overflowMenu?.offsetHeight || 0;
        finalHeight = Math.max(overflowHeight, overflowListHeight);
      } else {
        finalHeight = overflowListHeight;
      }
    }

    if (!submenu) {
      finalHeight = 0;
    }

    return finalHeight;
  }

  /**
   * Keep clip height in sync while the submenu is open (hydration, late images, wrap).
   * First write waits two frames so content-visibility can leave contain-intrinsic-size;
   * ResizeObserver starts only after that settled measure.
   * @param {HTMLElement} submenu
   * @param {boolean} isDefaultSlot
   * @param {boolean} hasSubmenu
   */
  #observeSubmenuHeight(submenu, isDefaultSlot, hasSubmenu) {
    this.#cleanupSubmenuHeightObserver();

    const isCurrentSubmenu = () => {
      const activeSubmenu = findSubmenu(this.#state.activeItem) ?? (!isDefaultSlot ? this.overflowMenu : null);

      return activeSubmenu === submenu;
    };

    const applyIfStillActive = () => {
      if (!this.#state.activeItem || !isCurrentSubmenu()) return;

      const height = this.#measureSubmenuHeight(submenu, isDefaultSlot, hasSubmenu);

      // Observer path must not collapse the panel; only #deactivate writes 0.
      if (height === 0) return;

      this.#applySubmenuHeight(height);
    };

    const startObserver = () => {
      this.#submenuHeightRafId = 0;

      if (!isCurrentSubmenu()) return;

      applyIfStillActive();

      this.#submenuResizeObserver = new ResizeObserver(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(applyIfStillActive);
        });
      });
      this.#submenuResizeObserver.observe(submenu);
    };

    // Double rAF so content-visibility: visible can settle past contain-intrinsic-size
    this.#submenuHeightRafId = requestAnimationFrame(() => {
      this.#submenuHeightRafId = requestAnimationFrame(startObserver);
    });
  }

  #cleanupSubmenuHeightObserver() {
    if (this.#submenuHeightRafId) {
      cancelAnimationFrame(this.#submenuHeightRafId);
      this.#submenuHeightRafId = 0;
    }

    this.#submenuResizeObserver?.disconnect();
    this.#submenuResizeObserver = null;
  }

  /**
   * Calculate and set the full open header height. If the submenu is not open, the full open header height is 0.
   * @param {number} submenuHeight
   */
  #setFullOpenHeaderHeight(submenuHeight) {
    if (!this.headerComponent) return;

    const isOverlapSituation = this.headerComponent.hasAttribute('data-submenu-overlap-bottom-row');

    const headerVisibleHeight =
      isOverlapSituation && this.headerComponent.offsetHeight > 0
        ? /** @type {HTMLElement | null} */ (this.headerComponent.querySelector('.header__row--top'))?.offsetHeight ?? 0
        : this.headerComponent.offsetHeight;

    const nothingToOpen = submenuHeight === 0;
    const fullOpenHeaderHeight = nothingToOpen ? 0 : submenuHeight + (headerVisibleHeight ?? 0);

    this.headerComponent?.style.setProperty('--full-open-header-height', `${fullOpenHeaderHeight}px`);
  }

  /**
   * Preload images that are set to load lazily.
   * @param {ParentNode} [root]
   */
  #preloadImages = (root = this) => {
    const images = root.querySelectorAll('img[loading="lazy"]');

    for (const image of images) {
      image.removeAttribute('loading');
    }
  };

  #addDismissListeners() {
    this.addEventListener('keyup', this.#handleKeyUp);
    document.addEventListener('click', this.#handleClickOutside);
    document.addEventListener('focusin', this.#handleFocusOutside);
    window.addEventListener('scroll', this.#handleScroll, {passive: true});
  }

  #removeDismissListeners() {
    this.removeEventListener('keyup', this.#handleKeyUp);
    document.removeEventListener('click', this.#handleClickOutside);
    document.removeEventListener('focusin', this.#handleFocusOutside);
    window.removeEventListener('scroll', this.#handleScroll);
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    const activeItem = this.#state.activeItem;

    this.#deactivate(activeItem, {force: true});
    activeItem?.focus();
    event.stopPropagation();
  };

  /**
   * @param {MouseEvent} event
   */
  #handleClickOutside = (event) => {
    if (isEventInsideComponent(event, this)) return;

    this.#deactivate(this.#state.activeItem, {force: true});
  };

  /**
   * @param {FocusEvent} event
   */
  #handleFocusOutside = (event) => {
    if (isEventInsideComponent(event, this)) return;

    this.#deactivate(this.#state.activeItem, {force: true});
  };

  #handleScroll = () => {
    if (!this.#isDesktopClickMode || !this.#state.activeItem) return;

    this.#deactivate(this.#state.activeItem, {force: true});
  };
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the closest menu item.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;

  let searchRoot = element;

  // Resolve "More" interactions to the overflow item without recursion.
  const moreItem = element.closest('[slot="more"]');
  if (moreItem) {
    const overflowItem = moreItem.parentElement?.querySelector('[slot="overflow"]');

    if (!(overflowItem instanceof Element) || overflowItem === moreItem) {
      return null;
    }

    searchRoot = overflowItem;
  }

  // When the element itself carries the ref (e.g. the toggle button in click mode), return it directly
  const self = searchRoot.closest('[ref="menuitem"]');
  if (self instanceof HTMLElement) return self;

  const descendant = searchRoot.querySelector('[ref="menuitem"]');
  return descendant instanceof HTMLElement ? descendant : null;
}

/**
 * Find the closest submenu.
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findSubmenu(element) {
  const submenu = element?.parentElement?.querySelector('[ref="submenu[]"]');
  return submenu instanceof HTMLElement ? submenu : null;
}

/**
 * Whether an event occurred within the component, including shadow content.
 * @param {Event} event
 * @param {HeaderMenu} component
 * @returns {boolean}
 */
function isEventInsideComponent(event, component) {
  return event.composedPath().includes(component);
}

/**
 * Focus the first interactive element within a container.
 * @param {Element} container
 */
function focusFirstFocusableElement(container) {
  const firstFocusable = container.querySelector(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
  );

  if (firstFocusable instanceof HTMLElement) {
    firstFocusable.focus();
  }
}
